import {
  clearAtsReportsDrillDown,
  setBreakdown,
  setGroupBy,
  setMetric,
} from '@/store/reports/ats';
import { fetchAtsReportsDrillDown } from '@/store/reports/ats/actions';
import { Flex, Icon, Td, Text, Tr } from '@chakra-ui/react';
import { ArrowRightIcon } from '@heroicons/react/outline';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CandidatesPopup } from '../../team-productivity/components/candidates';
import { Link } from '../../team-productivity/components/link';
import { Table } from '../../team-productivity/components/table';
import { Menu } from '../components/menu';
import { BREAKDOWN, GROUP_BY, METRICS } from '../constants/groupBy';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';

export const secondsToHumanTime = (data) => {
  if (!data || !isFinite(data) || isNaN(data)) {
    return '-';
  }

  const totalSeconds = Math.round(data);

  const DAY_IN_SECONDS = 24 * 60 * 60;
  const HOUR_IN_SECONDS = 60 * 60;
  const MINUTE_IN_SECONDS = 60;

  if (totalSeconds >= DAY_IN_SECONDS) {
    const days = Math.round(totalSeconds / DAY_IN_SECONDS);
    return `${days} day${days !== 1 ? 's' : ''}`;
  } else if (totalSeconds >= HOUR_IN_SECONDS) {
    const hours = Math.round(totalSeconds / HOUR_IN_SECONDS);
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  } else if (totalSeconds >= MINUTE_IN_SECONDS) {
    const minutes = Math.round(totalSeconds / MINUTE_IN_SECONDS);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else {
    return `${totalSeconds} second${totalSeconds !== 1 ? 's' : ''}`;
  }
};

export const AtsTable = () => {
  const dispatch = useDispatch();

  const {
    isLoading,
    filterBy,
    groupBy,
    breakdown,
    metric,
    columns: dataColumns,
    data,
  } = useSelector((state) => state?.atsReport);

  const { from, to } = useSelector((state) => state?.flowHealthReport?.filters);

  const [selected, setSelected] = useState();
  useEffect(() => {
    if (selected) {
      const params = {
        filter_by: {
          date: {
            from,
            // throws an error if "to" is null,
            to: to ?? undefined,
          },
          ...filterBy,
        },
        date_group: 'Q',
        group_by_1: groupBy?.id,
        group_by_2: breakdown?.id,
        drilldown: selected,
      };
      dispatch(fetchAtsReportsDrillDown(params));
    } else {
      dispatch(clearAtsReportsDrillDown());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const drillDown = useSelector((state) => state?.atsReport?.drillDown);

  const columns = useMemo(() => {
    return dataColumns
      .filter(({ key }) => {
        if (
          metric.id === 'passthrough_rate' &&
          (key.endsWith('_overall_rate') || key.endsWith('_time_in_stage'))
        ) {
          return false;
        }
        if (
          metric.id === 'overall_rate' &&
          (key.endsWith('_passthrough_rate') || key.endsWith('_time_in_stage'))
        ) {
          return false;
        }
        if (metric.id === 'time_in_stage' && key.endsWith('_rate')) {
          return false;
        }
        return true;
      })
      .map((column) => {
        const { key } = column;
        if (key === 'g1') {
          return {
            ...column,
            key: 'g1.name',
            sticky: true,
            sortable: true,
          };
        } else if (
          key.endsWith('_passthrough_rate') ||
          key.endsWith('_overall_rate')
        ) {
          return {
            ...column,
            render: (rate) => {
              const color =
                rate > 50 ? '#00A91B' : rate > 25 ? '#FF9900' : '#FF0000';
              const isValid = isFinite(rate) && rate > 0;
              const showArrow = key.endsWith('_passthrough_rate');
              return (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  columnGap="4px"
                >
                  {isValid ? (
                    <>
                      <Text
                        color={color}
                        fontWeight={breakdown?.id ? 700 : 500}
                      >
                        {rate}%
                      </Text>{' '}
                      {showArrow ? (
                        <Icon as={ArrowRightIcon} color={color} />
                      ) : null}
                    </>
                  ) : (
                    <Text>-</Text>
                  )}
                </Flex>
              );
            },
          };
        } else if (key.endsWith('_time_in_stage')) {
          return {
            ...column,
            render: (seconds) => {
              return (
                <Text fontWeight={breakdown?.id ? 700 : 500}>
                  {secondsToHumanTime(seconds)}
                </Text>
              );
            },
          };
        } else if (key.endsWith('_total_count')) {
          return {
            ...column,
            sortable: true,
            render: (count, row) => {
              return (
                <Link
                  onClick={
                    count === 0
                      ? undefined
                      : () => {
                          setSelected({
                            g1: row.g1.id,
                            metric: key.replace('_total', ''),
                            title: `Candidates in ${column.name} stage`,
                            description: `List of candidates in the ${column.name} stage in the selected time period`,
                          });
                        }
                  }
                  text={count}
                  fontWeight={breakdown?.id ? 700 : 500}
                />
              );
            },
          };
        }

        return column;
      });
  }, [dataColumns, metric?.id, breakdown?.id]);

  return (
    <Flex flexDirection="column" rowGap="8px">
      <Flex width="100%" justifyContent="flex-end" columnGap="8px">
        <Menu
          options={GROUP_BY}
          prefix="Group by:"
          value={groupBy}
          onChange={(groupBy) => dispatch(setGroupBy(groupBy))}
        />
        <Menu
          options={BREAKDOWN}
          prefix="Breakdown by:"
          value={breakdown ?? { name: '-' }}
          onChange={(breakdown) => dispatch(setBreakdown(breakdown))}
        />
        <Menu
          options={METRICS}
          prefix="Metric:"
          value={metric}
          onChange={(metric) => dispatch(setMetric(metric))}
        />
      </Flex>
      <Table
        columns={columns}
        data={data}
        showPerPage={false}
        isLoading={isLoading}
        defaultSort={['g1.name', 'asc']}
        expand={{
          canRowExpand: () => !!breakdown?.id,
          onExpand: ({ g2 = [], g1 }, rowInx) => {
            return (
              <>
                {g2.map((innerRow, inx) => {
                  return (
                    <Tr key={`row-${rowInx}-inner-${inx}`}>
                      {columns.map(({ key, sticky, name }, inx) => {
                        return (
                          <Td
                            {...(sticky
                              ? {
                                  position: 'sticky',
                                  left: 0,
                                  backgroundColor: '#FFFFFF',
                                  minWidth: '164px',
                                  maxWidth: '164px',
                                }
                              : {})}
                            border="1px solid #EBEBEC"
                            padding="8px"
                            key={`inner-col-${inx}`}
                          >
                            <Flex
                              flex={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              {(() => {
                                if (key === 'g1.name') {
                                  return (
                                    <TruncatedTextWithTooltip
                                      text={innerRow['g2']['name']}
                                      fontSize="12px"
                                      alignSelf="flex-start"
                                      marginLeft="15%"
                                      overflow="hidden"
                                      textOverflow="ellipsis"
                                      flexGrow={1}
                                    />
                                  );
                                }
                                if (
                                  key.endsWith('_passthrough_rate') ||
                                  key.endsWith('_overall_rate')
                                ) {
                                  const rate = innerRow[key];
                                  const isValid = isFinite(rate) && rate > 0;
                                  const showArrow =
                                    key.endsWith('_passthrough_rate');
                                  const color =
                                    rate > 50
                                      ? '#00A91B'
                                      : rate > 25
                                      ? '#FF9900'
                                      : '#FF0000';
                                  return (
                                    <Flex
                                      alignItems="center"
                                      justifyContent="center"
                                      columnGap="4px"
                                    >
                                      {isValid ? (
                                        <>
                                          <Text fontSize="12px" color={color}>
                                            {rate}%
                                          </Text>{' '}
                                          {showArrow ? (
                                            <Icon
                                              as={ArrowRightIcon}
                                              fontSize="12px"
                                              color={color}
                                            />
                                          ) : null}
                                        </>
                                      ) : (
                                        <Text>-</Text>
                                      )}
                                    </Flex>
                                  );
                                }
                                if (key.endsWith('_time_in_stage')) {
                                  return (
                                    <Text fontSize="12px">
                                      {secondsToHumanTime(innerRow[key])}
                                    </Text>
                                  );
                                }
                                if (key.endsWith('_total_count')) {
                                  const count = innerRow[key];
                                  return (
                                    <Link
                                      onClick={
                                        count === 0
                                          ? undefined
                                          : () => {
                                              setSelected({
                                                g1: g1.id,
                                                g2: innerRow.g2.id,
                                                metric: key.replace(
                                                  '_total',
                                                  ''
                                                ),
                                                title: `Candidates in ${name} stage`,
                                                description: `List of candidates in the ${name} stage in the selected time period`,
                                              });
                                            }
                                      }
                                      fontSize="12px"
                                      text={count}
                                    />
                                  );
                                }
                                return (
                                  <Text fontSize="12px">{innerRow[key]}</Text>
                                );
                              })()}
                            </Flex>
                          </Td>
                        );
                      })}
                    </Tr>
                  );
                })}
              </>
            );
          },
        }}
      />

      <CandidatesPopup
        isOpen={!!selected}
        onClose={() => setSelected(undefined)}
        {...drillDown}
        title={selected?.title ?? ''}
        description={selected?.description ?? ''}
      />
    </Flex>
  );
};
